/**
 * Hook für Version History
 * 
 * Lädt Versionen für verschiedene Entity-Typen mit TanStack Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  getContentBlockVersions,
  rollbackContentBlockVersion,
  getContentBlockVersionDiff,
  getChannelPackageVersions,
  rollbackChannelPackageVersion,
} from '@/lib/api/versions'
import type { 
  ContentBlockVersion, 
  VersionHistoryData 
} from '@/types/versioning'
import type { PublishingSchedule } from '@/types/publishing'

export type VersionEntityType = 'content_block' | 'channel_package' | 'product_asset_link'

interface UseVersionHistoryOptions {
  entityType: VersionEntityType
  entityId: string
  channelId?: string  // Required for channel_package
  enabled?: boolean
}

/**
 * Hook to fetch version history for an entity
 */
export function useVersionHistory({
  entityType,
  entityId,
  channelId,
  enabled = true,
}: UseVersionHistoryOptions) {
  return useQuery({
    queryKey: ['versions', entityType, entityId, channelId],
    queryFn: async () => {
      switch (entityType) {
        case 'content_block':
          return getContentBlockVersions(entityId)
        case 'channel_package':
          if (!channelId) throw new Error('channelId required for channel_package')
          return getChannelPackageVersions(channelId, entityId)
        default:
          throw new Error(`Unsupported entity type: ${entityType}`)
      }
    },
    enabled: enabled && !!entityId,
    staleTime: 30_000, // 30 seconds
  })
}

/**
 * Hook to group versions into current, scheduled, upcoming, and history
 */
export function useGroupedVersionHistory(
  versions: ContentBlockVersion[] | undefined,
  currentVersionId: string | null | undefined,
  activeSchedule: PublishingSchedule | null | undefined
): VersionHistoryData {
  if (!versions) {
    return {
      current: null,
      scheduled: null,
      upcoming: [],
      history: [],
    }
  }

  const current = versions.find(v => v.id === currentVersionId) || null
  const scheduled = activeSchedule 
    ? versions.find(v => v.id === activeSchedule.version_id) || null
    : null
  
  // History = all versions except current and scheduled
  const history = versions.filter(v => 
    v.id !== currentVersionId && 
    v.id !== activeSchedule?.version_id
  ).sort((a, b) => b.version_number - a.version_number)

  return {
    current,
    scheduled,
    upcoming: [], // Future: Could be populated from upcoming schedules
    history,
  }
}

/**
 * Hook for rollback mutation
 */
export function useRollbackVersion(
  entityType: VersionEntityType,
  entityId: string,
  channelId?: string
) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async (versionId: string) => {
      switch (entityType) {
        case 'content_block':
          return rollbackContentBlockVersion(entityId, versionId)
        case 'channel_package':
          if (!channelId) throw new Error('channelId required for channel_package')
          return rollbackChannelPackageVersion(channelId, entityId, versionId)
        default:
          throw new Error(`Unsupported entity type: ${entityType}`)
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.versionRestored'))
        // Invalidate related queries
        queryClient.invalidateQueries({ 
          queryKey: ['versions', entityType, entityId] 
        })
        queryClient.invalidateQueries({ 
          queryKey: ['content-blocks'] 
        })
        if (entityType === 'content_block') {
          queryClient.invalidateQueries({ 
            queryKey: ['channel-packages'] 
          })
        }
      } else {
        toast.error(data.error || t('toast.error.restoring'))
      }
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`)
    },
  })
}

/**
 * Hook for version diff
 */
export function useVersionDiff(
  entityType: VersionEntityType,
  entityId: string,
  versionAId: string | null,
  versionBId: string | null,
  _channelId?: string
) {
  return useQuery({
    queryKey: ['version-diff', entityType, entityId, versionAId, versionBId],
    queryFn: async () => {
      if (!versionAId || !versionBId) {
        throw new Error('Both version IDs required')
      }
      
      switch (entityType) {
        case 'content_block':
          return getContentBlockVersionDiff(entityId, versionAId, versionBId)
        default:
          throw new Error(`Diff not supported for entity type: ${entityType}`)
      }
    },
    enabled: !!versionAId && !!versionBId,
    staleTime: 60_000, // 1 minute (diffs don't change)
  })
}

